import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Shared HTML preview UI used by:
 *   - `ContentMasterProductPage` (single-product editor, original / draft fields)
 *   - `ContentMasterBulkReviewPage` (per-card original / generated diffs)
 *
 * Exports:
 *   - `HTML_PREVIEW_CORE_FIELDS` - the canonical set of core fields whose
 *     values are expected to contain HTML markup (`shortDescription`,
 *     `description`). Used to gate the preview button.
 *   - `ContentMasterEyeIcon` - the small eye glyph rendered next to a field
 *     label when `HTML_PREVIEW_CORE_FIELDS.has(field)`.
 *   - `useHtmlPreview()` - minimal state-management hook returning
 *     `{ open, close, modal }`. `modal` is the JSX you splat into the page;
 *     `open(title, html)` opens it, `close()` closes it.
 *
 * Behaviour mirrors the legacy inline modal that lived inside
 * `ContentMasterProductPage`: a fixed overlay, three width presets
 * (mobile 375px / tablet 768px / desktop 100%), a sandbox-restricted iframe
 * with `srcDoc` so external scripts and forms inside user HTML are blocked.
 */

export type HtmlPreviewWidth = 'mobile' | 'tablet' | 'desktop';

export const HTML_PREVIEW_WIDTH_PX: Record<HtmlPreviewWidth, string> = {
  mobile: '375px',
  tablet: '768px',
  desktop: '100%',
};

/** Core API field names whose values render as HTML. */
export const HTML_PREVIEW_CORE_FIELDS: ReadonlySet<string> = new Set([
  'shortDescription',
  'description',
]);

/** Inline SVG eye glyph used for the per-field preview button. */
export const ContentMasterEyeIcon: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M12 5c-7 0-10 7-10 7s3 7 10 7 10-7 10-7-3-7-10-7zm0 11a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm0-6a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"
      fill="currentColor"
    />
  </svg>
);

/**
 * Wraps user HTML in a minimal document with a sane reset / typography so
 * the iframe preview matches what shop fronts typically render.
 */
const buildPreviewSrcDoc = (html: string): string => {
  const safeHtml = typeof html === 'string' ? html : '';
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<style>
  html, body { margin: 0; padding: 0; }
  body { padding: 16px; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif; font-size: 14px; color: #111827; line-height: 1.5; word-wrap: break-word; }
  img { max-width: 100%; height: auto; }
  table { max-width: 100%; }
</style>
</head>
<body>${safeHtml}</body>
</html>`;
};

interface HtmlPreviewState {
  open: boolean;
  title: string;
  html: string;
  width: HtmlPreviewWidth;
}

const INITIAL: HtmlPreviewState = {
  open: false,
  title: '',
  html: '',
  width: 'desktop',
};

export interface UseHtmlPreviewResult {
  /** Open the preview with a given heading + HTML body. */
  open: (title: string, html: string) => void;
  close: () => void;
  /** JSX to splat into the page (only renders when open). */
  modal: React.ReactElement | null;
}

/**
 * Hook that owns the modal's local state and produces the JSX. The page
 * controls when to call `open(...)` from a per-field eye button.
 */
export function useHtmlPreview(): UseHtmlPreviewResult {
  const { t } = useTranslation();
  const [state, setState] = useState<HtmlPreviewState>(INITIAL);

  const open = useCallback((title: string, html: string) => {
    setState({ open: true, title, html, width: 'desktop' });
  }, []);
  const close = useCallback(() => {
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  const modal = state.open ? (
    <div
      className="content-master-modal-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          close();
        }
      }}
    >
      <div
        className="content-master-modal content-master-html-preview-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="content-master-html-preview-title"
      >
        <div className="content-master-modal__header">
          <h3 id="content-master-html-preview-title" className="content-master-modal__title">
            {t('content_master_preview_html_title', { field: state.title })}
          </h3>
          <button
            type="button"
            className="content-master-modal__close"
            onClick={close}
            aria-label={t('cancelText')}
          >
            ×
          </button>
        </div>

        <div className="content-master-html-preview-toolbar" role="group" aria-label="width">
          {(['mobile', 'tablet', 'desktop'] as const).map((w) => (
            <button
              key={w}
              type="button"
              className={`content-master-html-preview-width-btn ${
                state.width === w ? 'content-master-html-preview-width-btn--active' : ''
              }`}
              onClick={() => setState((prev) => ({ ...prev, width: w }))}
            >
              {t(`content_master_preview_width_${w}`)}
              {w !== 'desktop' ? ` (${HTML_PREVIEW_WIDTH_PX[w]})` : ''}
            </button>
          ))}
        </div>

        <div className="content-master-html-preview-body">
          <iframe
            title="content-master-html-preview"
            className="content-master-html-preview-frame"
            sandbox=""
            srcDoc={buildPreviewSrcDoc(state.html)}
            style={{ width: HTML_PREVIEW_WIDTH_PX[state.width] }}
          />
        </div>

        <div className="content-master-modal__footer">
          <button type="button" className="btn btn-secondary" onClick={close}>
            {t('cancelText')}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return { open, close, modal };
}
